import { type PointerEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { cssZoomOf } from "../marketing/siteScale";
import "./member-phone-story.css";
const STORIES = [
  {
    id: "calendar",
    title: "Calendar",
    body: "See your published shifts in month, week and day views. Add personal plans alongside your duties, and subscribe to your shifts from your calendar app.",
    screen: "member-calendar",
    alt: "Published shifts in the member calendar",
  },
  {
    id: "inbox",
    title: "Inbox",
    body: "Roster updates, swap requests and reminders arrive in one inbox. See what needs your response, check past updates and choose your email notification preferences.",
    screen: "member-inbox",
    alt: "Member inbox with updates and requests",
  },
  {
    id: "tasks",
    title: "Tasks & requests",
    body: "Submit leave and sick reports, follow your request status, and keep assigned work in one place. Send preferences and notes to your rosterer before the roster is built.",
    screen: "member-tasks",
    alt: "Member tasks and requests with status chips",
  },
  {
    id: "privacy",
    title: "Privacy",
    body: "Personal calendar blocks stay visible only to you. Share an assigned task's timing when you choose, revoke it when needed, and export your own data.",
    screen: "member-timeblocks",
    alt: "Personal calendar blocks beside shifts, visible only to the member",
  },
];
export function MemberPhoneStory() {
  const [active, setActive] = useState(0);
  const [lifted, setLifted] = useState<number | null>(null);
  const rootRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { hash } = useLocation();
  useLayoutEffect(() => {
    let frame = 0;
    const started = performance.now();
    const position = () => {
      const root = rootRef.current;
      const panel = panelRef.current;
      const phone = root?.querySelector(
        `[data-phone-slot="${lifted ?? active + 4}"] .member-story-phone`,
      );
      if (!root || !panel || !phone) return;
      // Rects come in screen px; the page root may carry the laptop zoom
      // (#2817), and the px written below are the root's own px.
      const z = cssZoomOf(root);
      const box = (el: Element) => {
        const r = el.getBoundingClientRect();
        return {
          left: r.left / z,
          right: r.right / z,
          top: r.top / z,
          bottom: r.bottom / z,
          width: r.width / z,
          height: r.height / z,
        };
      };
      const bounds = box(root);
      const resting = box(phone);
      const floatWidth = window.innerWidth <= 700 ? 180 : 250;
      const floatHeight = (floatWidth * 1386) / 780;
      let floatLeft = resting.left - bounds.left + resting.width / 2 - floatWidth / 2;
      const introEl = root.closest(".member-page-hero")?.querySelector(".member-page-intro");
      const intro = introEl ? box(introEl) : undefined;
      const floatTop = Math.max(
        12,
        resting.top - bounds.top + resting.height / 2 - floatHeight / 2,
      );
      if (
        intro &&
        bounds.top + floatTop < intro.bottom + 20 &&
        bounds.top + floatTop + floatHeight > intro.top
      )
        floatLeft = Math.max(floatLeft, intro.right - bounds.left + 24);
      floatLeft = Math.max(8, Math.min(floatLeft, bounds.width - floatWidth - 8));
      root.style.setProperty("--float-left", `${floatLeft}px`);
      root.style.setProperty("--float-top", `${floatTop}px`);
      root.style.setProperty(
        "--float-start-x",
        `${resting.left - bounds.left + resting.width / 2 - (floatLeft + floatWidth / 2)}px`,
      );
      root.style.setProperty(
        "--float-start-y",
        `${resting.top - bounds.top + resting.height / 2 - (floatTop + floatHeight / 2)}px`,
      );
      const target =
        lifted !== null
          ? {
              left: bounds.left + floatLeft,
              right: bounds.left + floatLeft + floatWidth,
              top: bounds.top + floatTop,
            }
          : resting;
      const gap = 20;
      const width = panel.offsetWidth;
      const right = target.right - bounds.left + gap;
      const slotBounds = phone.parentElement ? box(phone.parentElement) : undefined;
      const useRight =
        (slotBounds ? slotBounds.left + slotBounds.width / 2 : target.left) <
        bounds.left + bounds.width / 2;
      const left = useRight ? right : target.left - bounds.left - width - gap;
      panel.style.setProperty(
        "--panel-left",
        `${Math.max(8, Math.min(left, bounds.width - width - 8))}px`,
      );
      panel.style.setProperty(
        "--panel-top",
        `${Math.max(12, Math.min(target.top - bounds.top + 20, (root.querySelector(".member-phone-stage")?.clientHeight ?? 620) - panel.offsetHeight - 12))}px`,
      );
      if (performance.now() - started < 1100) frame = requestAnimationFrame(position);
    };
    position();
    window.addEventListener("resize", position);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", position);
    };
  }, [active, lifted]);
  useEffect(() => {
    const index = STORIES.findIndex((item) => `#${item.id}` === hash);
    if (index >= 0) setActive(index);
  }, [hash]);
  // The floating phone turns towards the pointer. Each move sets a target and
  // one frame loop eases towards it, so the turn stays steady.
  const aim = useRef({ x: 0, y: 0 });
  const now = useRef({ x: 0, y: 0 });
  const frameRef = useRef(0);
  useEffect(() => () => cancelAnimationFrame(frameRef.current), []);
  function ease() {
    // Set on the floating phone only, so the nine resting phones never restyle.
    const root = rootRef.current?.querySelector<HTMLElement>(".member-floating-phone");
    const a = aim.current;
    const n = now.current;
    n.x += (a.x - n.x) * 0.14;
    n.y += (a.y - n.y) * 0.14;
    const done = Math.abs(a.x - n.x) < 0.001 && Math.abs(a.y - n.y) < 0.001;
    if (done) {
      n.x = a.x;
      n.y = a.y;
    }
    root?.style.setProperty("--mp-x", n.x.toFixed(4));
    root?.style.setProperty("--mp-y", n.y.toFixed(4));
    frameRef.current = done ? 0 : requestAnimationFrame(ease);
  }
  function kick() {
    if (!frameRef.current) frameRef.current = requestAnimationFrame(ease);
  }
  function tilt(e: PointerEvent<HTMLElement>) {
    const phone = rootRef.current?.querySelector<HTMLElement>(".member-floating-phone");
    if (!phone || e.pointerType !== "mouse") return;
    const r = phone.getBoundingClientRect();
    const clamp = (v: number) => Math.max(-0.5, Math.min(0.5, v));
    aim.current = {
      x: clamp((e.clientX - (r.left + r.width / 2)) / (r.width * 2)),
      y: clamp((e.clientY - (r.top + r.height / 2)) / (r.height * 1.5)),
    };
    kick();
  }
  function untilt() {
    aim.current = { x: 0, y: 0 };
    kick();
  }

  const story = STORIES[active];
  if (!story) return null;
  return (
    <section
      ref={rootRef}
      className="member-phone-story"
      onPointerMove={tilt}
      onPointerLeave={untilt}
      data-media-slot="member-walkthrough"
      aria-label="Member app walkthrough"
    >
      <div className="member-phone-stage" aria-label="App screens for the selected story">
        <div className="member-phone-plane">
          {Array.from({ length: 9 }, (_, slot) => ({
            item: STORIES[(slot + 0) % STORIES.length],
            slot,
          })).map(({ item, slot }) =>
            item ? (
              <div
                className={`member-phone-slot member-phone-slot-${slot}`}
                data-phone-slot={slot}
                key={`phone-${slot}`}
                onPointerDown={() => {
                  setActive(slot % STORIES.length);
                  setLifted(slot);
                }}
                onMouseEnter={() => {
                  setActive(slot % STORIES.length);
                  setLifted(slot);
                }}
              >
                <button
                  type="button"
                  aria-label={
                    slot >= 4 && slot < 8
                      ? `Explore ${item.title}`
                      : `Preview ${item.title} ${slot}`
                  }
                  aria-pressed={active === slot % STORIES.length}
                  onFocus={() => {
                    setActive(slot % STORIES.length);
                    setLifted(slot);
                  }}
                  onClick={() => {
                    setActive(slot % STORIES.length);
                    setLifted(slot);
                  }}
                  className="member-story-phone"
                  data-active={active === slot % STORIES.length}
                  data-lifted={lifted === slot}
                >
                  <span className="member-phone-speaker" aria-hidden="true" />
                  <img
                    src={`/features/${item.screen}.png`}
                    alt={item.alt}
                    width={780}
                    height={1386}
                    decoding="async"
                  />
                </button>
              </div>
            ) : null,
          )}
        </div>
      </div>
      {lifted !== null && (
        <div className="member-floating-phone" key={lifted} aria-hidden="true">
          {/* The outer box rises out of the plane; the body inside turns to
              face the pointer, as the handsets do on How it works. */}
          <div className="member-floating-body">
            <i className="member-phone-key member-phone-key-a" />
            <i className="member-phone-key member-phone-key-b" />
            <i className="member-phone-key member-phone-key-c" />
            <span className="member-phone-speaker" />
            <img src={`/features/${story.screen}.png`} alt="" width={780} height={1386} />
            <span className="member-phone-glare" />
          </div>
        </div>
      )}
      <div ref={panelRef} className="member-story-corner">
        <section
          id="member-story-panel"
          aria-labelledby="member-story-heading"
          key={story.id}
          className="member-story-copy"
        >
          <h2 id="member-story-heading">{story.title}</h2>
          <p>{story.body}</p>
        </section>
      </div>
    </section>
  );
}
